import {Form} from '../../../../../stores/Form/Form';
import {isRTL} from '../../../../../Localization';
import {keys} from '../../../../../constants/KeyCodes';
import {IFormViewTab} from '../useFormView';
import {Column} from '../../../../../stores/Form/Column';
import {findDefaultTab} from '../../../../../stores/Form/utils';
import {
    changeFormRow,
    findControlInVerticalRows,
    findInNextTabs,
    findInPrevTabs,
    findInRow,
    findInRowReverse,
    findSiblingInNextSections,
    findSiblingInPrevSections,
    getControlField,
    getCursorPosition,
    searchInTab,
    searchInTabReverse
} from './navigationUtils';
import {formsStore} from '../../../../../stores/Form';

export const rtl = isRTL();

export type NavDataType = {
    Column: Column;
    ActiveTab?: string;
} | null;

const navKeyMap = {
    [keys.ARROW_RIGHT]: true,
    [keys.ARROW_LEFT]: true,
    [keys.ARROW_DOWN]: true,
    [keys.ARROW_UP]: true,
    [keys.HOME]: true,
    [keys.END]: true,
    [keys.ENTER]: true,
    [keys.TAB]: true,
    [keys.PAGE_UP]: true,
    [keys.PAGE_DOWN]: true
};

type CursorPositionType = {
    sectionIndex: number;
    rowIndex: number;
    columnIndex: number;
    tabId: string;
};

function getPrevAvailableField(
    tabs: IFormViewTab[],
    position: CursorPositionType
): {Column: Column; ActiveTab: string} | undefined {
    const {sectionIndex, rowIndex, columnIndex, tabId: currentTabId} = position;
    const currentTabIndex = tabs.findIndex(t => t.Id === currentTabId);
    if (currentTabIndex === -1) return undefined;

    // 1. Try in current tab (backwards)
    const resultInCurrent = searchInTabReverse(
        tabs[currentTabIndex],
        sectionIndex,
        rowIndex,
        columnIndex,
        true
    );
    if (resultInCurrent) return resultInCurrent;

    // 2. Try previous tabs (skip index 0, it's fixed)
    for (let i = currentTabIndex - 1; i >= 0; i--) {
        const prevTab = tabs[i];
        const lastSectionIndex = prevTab.Sections.length - 1;
        const lastRowIndex = prevTab.Sections[lastSectionIndex]?.Rows.length - 1 ?? -1;

        const result = searchInTabReverse(
            prevTab,
            lastSectionIndex,
            lastRowIndex,
            prevTab.Sections[lastSectionIndex]?.Rows[lastRowIndex]?.length ?? 0,
            false
        );

        if (result) return result;
    }

    return null;
}

const getNextAvailableField = (
    tabs: IFormViewTab[],
    position: CursorPositionType,
    openedTabID: string
): {Column: Column; ActiveTab: string} | undefined => {
    const {sectionIndex, rowIndex, columnIndex, tabId: currentTabId} = position;

    const currentTabIndex = tabs.findIndex(t => t.Id === currentTabId);
    if (currentTabIndex === -1) return undefined;

    // Try in current tab
    const resultInCurrent = searchInTab(
        tabs[currentTabIndex],
        tabs[currentTabIndex].Id,
        sectionIndex,
        rowIndex,
        columnIndex,
        true
    );

    if (resultInCurrent) return resultInCurrent;

    // Try in following tabs (skip index 0 because it's the fixed tab)
    const openTabIndex = tabs.findIndex(tab => tab.Id === openedTabID);

    for (let i = currentTabIndex + 1; i < tabs.length; i++) {
        if (i >= openTabIndex) {
            const nextTab = tabs[i];
            const result = searchInTab(nextTab, nextTab.Id, 0, 0, -1, false);
            if (result) return result;
        }
    }

    const nextTab = tabs[1];
    return searchInTab(nextTab, nextTab.Id, 0, 0, -1, false);
};

const getNextRowSibling = (
    tabs: IFormViewTab[],
    position: CursorPositionType,
    skipDisabled?: boolean
) => {
    const {tabId: currentTabId, sectionIndex, rowIndex, columnIndex} = position;

    const currentTabIndex = tabs.findIndex(t => t.Id === currentTabId);
    if (currentTabIndex === -1) return undefined;

    const currentTab = tabs[currentTabIndex];
    const currentSection = currentTab.Sections[sectionIndex];
    if (!currentSection) return undefined;

    const currentRow = currentSection.Rows[rowIndex];

    // Rule 1: Search in the same row
    const nextInSameRow = findInRow(currentRow, columnIndex + 1, skipDisabled);
    if (nextInSameRow) return {Column: nextInSameRow, ActiveTab: currentTab.Id};

    // Rule 2: search in the next sections of the current tab
    const nextInNextSections = findSiblingInNextSections(
        currentTab.Sections,
        sectionIndex,
        rowIndex,
        skipDisabled
    );

    if (nextInNextSections) return {Column: nextInNextSections, ActiveTab: currentTab.Id};

    // Rule 3: Search in the first section of the next tab
    const nextInNextTabs = findInNextTabs(tabs, currentTabIndex, rowIndex, skipDisabled);
    if (nextInNextTabs) return nextInNextTabs;

    return null;
};

const getPrevRowSibling = (
    tabs: IFormViewTab[],
    position: CursorPositionType,
    skipDisabled?: boolean
) => {
    const {tabId, sectionIndex, rowIndex, columnIndex} = position;

    const currentTabIndex = tabs.findIndex(tab => tab.Id === tabId);
    if (currentTabIndex === -1) return undefined;

    const currentTab = tabs[currentTabIndex];
    const currentSection = currentTab.Sections[sectionIndex];
    const currentRow = currentSection?.Rows?.[rowIndex];

    // 1. Try to go left in the same row
    const prevInRow = findInRowReverse(currentRow, columnIndex, skipDisabled);
    if (prevInRow) return {Column: prevInRow};

    // 2. Try previous sections in the current tab
    const prevInSections = findSiblingInPrevSections(
        currentTab.Sections,
        sectionIndex,
        rowIndex,
        skipDisabled
    );
    if (prevInSections) return {Column: prevInSections};

    // 3. Try previous tabs
    const prevInTabs = findInPrevTabs(tabs, currentTabIndex, rowIndex, skipDisabled);
    if (prevInTabs) return prevInTabs;

    return null;
};

const getNextTabFirstColumn = (tabs: IFormViewTab[], openTabId: string) => {
    const tabIndex = tabs.findIndex(tab => tab.Id === openTabId);
    const nextTab = tabs[tabIndex + 1] || tabs[1];

    return {ActiveTab: nextTab.Id, Column: getTabFirstColumn(nextTab)};
};

const getPrevTabFirstColumn = (tabs: IFormViewTab[], openTabId: string) => {
    const tabIndex = tabs.findIndex(tab => tab.Id === openTabId);
    const prevTabIndex = tabIndex - 1;
    const nextTab = prevTabIndex >= 1 ? tabs[prevTabIndex] : tabs[tabs.length - 1];

    return {ActiveTab: nextTab.Id, Column: getTabFirstColumn(nextTab)};
};

export const getUpperControl = (
    tab: IFormViewTab,
    position: CursorPositionType
): {Column: Column} | null | undefined => {
    const {sectionIndex, rowIndex, columnIndex} = position;
    const section = tab.Sections[sectionIndex];

    if (!section || rowIndex === 0) return null;

    // Traverse upward
    return findControlInVerticalRows(section.Rows.slice(0, rowIndex), columnIndex, 'up');
};

export const getBellowControl = (
    tab: IFormViewTab,
    position: CursorPositionType
): {Column: Column} | null | undefined => {
    const {sectionIndex, rowIndex, columnIndex} = position;
    const section = tab.Sections[sectionIndex];

    if (!section) return null;

    // Rule 4: If last row, return current control if available
    if (rowIndex >= section.Rows.length - 1) {
        const control = section.Rows?.[rowIndex]?.[columnIndex];
        return getControlField(control);
    }
    // Traverse downward
    return findControlInVerticalRows(section.Rows.slice(rowIndex + 1), columnIndex, 'down');
};

const getTabFirstColumn = (tab: IFormViewTab) => {
    return tab.Sections?.[0].Rows?.[0]?.[0].Column;
};

const getLastColumn = (tab: IFormViewTab) => {
    const lastSection = tab.Sections[tab.Sections.length - 1];
    const lastRow = lastSection.Rows[lastSection.Rows.length - 1];
    return lastRow[lastRow.length - 1].Column;
};

export const getRecordDialogFlag = () => formsStore.currentScreen?.isRecordDialog;

const getNavigationData = async (
    e: KeyboardEvent,
    form: Form,
    allTabs: IFormViewTab[],
    openTabId: string
): Promise<NavDataType> => {
    const {currentColumnId, currentColumn} = form;
    const isEditColumnMode = currentColumn?.editMode;
    const isSideArrow = e.key === keys.ARROW_LEFT || e.key === keys.ARROW_RIGHT;

    if (!isEditColumnMode) {
        e.preventDefault();
    } else if (!isSideArrow) {
        // Prevent default behavior for other keys in edit mode, except side arrows
        e.preventDefault();
    }

    if (e.key === keys.PAGE_UP) {
        if (getRecordDialogFlag()) return null;
        const newRowIndex = Math.max(0, form.currentRow - 1);
        changeFormRow(newRowIndex, form);
        return null;
    } else if (e.key === keys.PAGE_DOWN) {
        if (getRecordDialogFlag()) return null;
        const newRowIndex = Math.min(form.records.length - 1, form.currentRow + 1);
        changeFormRow(newRowIndex, form);
        return null;
    }

    const fixedTab = allTabs[0];
    const tabIndex = allTabs.findIndex(tab => tab.Id === openTabId);
    const tab = allTabs[tabIndex];
    const visibleTabs = [fixedTab, tab];

    const activeTab = findDefaultTab(visibleTabs, currentColumnId, false);
    const position = getCursorPosition(visibleTabs, currentColumnId);

    if (!position) return null;

    if (e.key === keys.ARROW_LEFT) {
        if (isEditColumnMode) return null;

        if (e.shiftKey) {
            const handler = rtl ? getNextTabFirstColumn : getPrevTabFirstColumn;
            return handler(allTabs, openTabId);
        }

        const navigationHandler = rtl ? getNextRowSibling : getPrevRowSibling;
        return navigationHandler(visibleTabs, position, false);
    } else if (e.key === keys.ARROW_RIGHT) {
        if (isEditColumnMode) return null;

        if (e.shiftKey) {
            const handler = rtl ? getPrevTabFirstColumn : getNextTabFirstColumn;
            return handler(allTabs, openTabId);
        }

        const navigationHandler = rtl ? getPrevRowSibling : getNextRowSibling;
        return navigationHandler(visibleTabs, position, false);
    } else if (e.key === keys.ARROW_UP) {
        return getUpperControl(activeTab, position);
    } else if (e.key === keys.ARROW_DOWN) {
        return getBellowControl(activeTab, position);
    } else if (e.key === keys.HOME) {
        if (e.ctrlKey || e.metaKey) {
            changeFormRow(0, form);
            return null;
        }
        return {Column: getTabFirstColumn(activeTab)};
    } else if (e.key === keys.END) {
        if (e.ctrlKey || e.metaKey) {
            changeFormRow(form.records.length - 2, form);
            return null;
        }
        return {
            Column: getLastColumn(tab),
            ActiveTab: tab?.Id
        };
    } else if (e.key === keys.TAB || e.key === keys.ENTER) {
        if (e.key === keys.ENTER && Boolean(form.searchModeLevel)) {
            return null;
        }

        if (e.shiftKey && e.key === keys.TAB) {
            return getPrevAvailableField(visibleTabs, position);
        } else {
            return getNextAvailableField(allTabs, position, openTabId);
        }
    }
    return null;
};

export const navigation = async (
    e: KeyboardEvent,
    form: Form,
    tabs: IFormViewTab[],
    openTabId: string,
    callback?: (params: NavDataType) => void
) => {
    if (navKeyMap[e.key] && !!form.formDesign.rowViewColumnsDesign.length && !form.isApiLock()) {
        const navData = await getNavigationData(e, form, tabs, openTabId);

        if (callback && navData) {
            callback(navData);
        }
    }
};
